import * as React from "react";
import { Tabs as ReactUITabs } from "@skbkontur/react-ui/cjs/components/Tabs/Tabs";

import { clampUInt } from "../../../../../Common/IntRangeUtils";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";

import { FixedTabsContextProvider, IFixedTabsContext } from "./FixedTabsContext";

export interface FixedTabsProps {
    children: React.ReactNode;
    defaultTab: undefined | string;
    defaultTabByValue: undefined | string;
    defaultTabNameInInner?: string;
    "data-tid"?: string;
}

export interface TabInfo {
    id: string;
    renderTitle: () => React.ReactNode;
}

export function FixedTabs(props: FixedTabsProps): JSX.Element {
    const [currentTab, setCurrentTab] = React.useState<string | undefined>();
    const [tabs, setTabs] = React.useState<TabInfo[]>([]);
    const defaultTabByPath = React.useRef<string | undefined>(undefined);

    const registerCallback = React.useCallback(
        (id: string, renderTitle: () => React.ReactNode): (() => void) => {
            setTabs(list => [...list, { id: id, renderTitle: renderTitle }]);
            setCurrentTab(prevId => prevId ?? id);
            return () => setTabs(list => list.filter(x => x.id !== id));
        },
        [tabs, currentTab]
    );

    const currentIdIndex = React.useRef<number | undefined>(undefined);

    React.useEffect(() => {
        setTimeout(() => {
            setCurrentTab(props.defaultTab);
        }, 0);
    }, []);

    React.useEffect(() => {
        if (tabs.length === 0) {
            setCurrentTab(undefined);
        }
        if (!tabs.some(x => x.id === currentTab)) {
            const prevIndex = currentIdIndex.current;
            if (prevIndex != undefined) {
                setCurrentTab(tabs[clampUInt(prevIndex, 0, tabs.length - 1)]?.id);
            } else {
                setCurrentTab(tabs[0]?.id);
            }
        }

        currentIdIndex.current = tabs.findIndex(x => x.id === currentTab);
    }, [tabs, currentTab]);

    React.useEffect(() => {
        if (
            tabs.some(tab => tab.id === props.defaultTabNameInInner) &&
            defaultTabByPath.current !== props.defaultTabNameInInner
        ) {
            defaultTabByPath.current = props.defaultTabNameInInner;
            setCurrentTab(props.defaultTabNameInInner);
        }
    }, [props.defaultTabNameInInner, tabs]);

    const fixedTabsContext = React.useMemo<IFixedTabsContext>(
        () => ({
            register: registerCallback,
            currentId: currentTab,
            setCurrentTab: setCurrentTab,
        }),
        [registerCallback, currentTab]
    );

    const pageObjectId = React.useMemo(
        () => (props["data-tid"] ? `FixedTabs/${props["data-tid"]}` : `FixedTabs`),
        [props["data-tid"]]
    );

    const testPageObject = React.useMemo(
        () => ({
            getType: () => "FixedTabs",
            getInstances: () => tabs.map(item => item.id),
            setPage: (instanceId: string) => {
                if (tabs.some(x => x.id === instanceId)) {
                    setCurrentTab(instanceId);
                    return true;
                }
                return false;
            },
        }),
        [tabs]
    );

    return (
        <TestPageObjectHandle id={pageObjectId} pageObject={testPageObject}>
            <FixedTabsContextProvider value={fixedTabsContext}>
                <div data-tid={props["data-tid"] ?? "Root_Fixed_Tabs"} style={{ position: "relative" }}>
                    <div data-tid="Items">
                        <ReactUITabs value={currentTab ?? ""} onValueChange={setCurrentTab}>
                            {tabs.map(tab => (
                                <ReactUITabs.Tab id={tab.id} key={tab.id}>
                                    <>{tab.renderTitle()}</>
                                </ReactUITabs.Tab>
                            ))}
                        </ReactUITabs>
                    </div>
                    {props.children}
                </div>
            </FixedTabsContextProvider>
        </TestPageObjectHandle>
    );
}
